import { useState } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Lightbox from '../components/Lightbox';

// ── Stills ──
import sti01 from '../imagesForCategory/Stills/Sti 01.jpg';
import sti02 from '../imagesForCategory/Stills/Sti 02.jpg';
import sti03 from '../imagesForCategory/Stills/Sti 03.jpg';
import sti04 from '../imagesForCategory/Stills/Sti 04.jpg';
import sti05 from '../imagesForCategory/Stills/Sti 05.JPG';
import sti06 from '../imagesForCategory/Stills/Sti 06.JPG';
import sti07 from '../imagesForCategory/Stills/Sti 07.jpg';

const allImages = [sti01, sti02, sti03, sti04, sti05, sti06, sti07];

export default function Stills() {
  useScrollAnimation();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <main>
      {/* ═══ INTRO ═══ */}
      <section className="py-20 md:py-28 bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-up">
          <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
            The Art of Stillness
          </p>
          <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-4">
            Every Frame, A Story
          </h2>
          <div className="gold-line mb-6" />
          <p className="text-cream/55 text-sm md:text-base leading-[1.8] font-light max-w-3xl mx-auto">
            Still photography is where art meets emotion. From powerful portraits to thoughtfully composed scenes,
            our stills capture personality, mood, and character in every single frame. With expert lighting,
            creative direction, and decades of experience, we create images that are not just photographs —
            they are visual stories that stand the test of time.
          </p>
        </div>
      </section>

      {/* ═══ GALLERY ═══ */}
      <section className="py-20 md:py-28 bg-charcoal-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 md:mb-16 text-center fade-up">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
              Our Portfolio
            </p>
            <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-4">
              Gallery
            </h2>
            <div className="gold-line mb-6" />
          </div>

          {/* Masonry-style grid */}
          <div className="space-y-4">
            {/* Top row — hero + side */}
            <div className="grid md:grid-cols-12 gap-4">
              <div
                className="md:col-span-7 album-card scale-up aspect-[4/3] md:aspect-auto md:min-h-[500px] cursor-pointer"
                onClick={() => setLightboxIdx(0)}
              >
                <img src={allImages[0]} alt="Stills featured" loading="lazy" />
                <div className="overlay">
                  <div>
                    <span className="text-cream text-xs font-montserrat tracking-[0.1em] uppercase block mb-1">Featured</span>
                    <span className="text-cream/50 text-[10px] font-montserrat">1 of {allImages.length}</span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-5 grid grid-cols-2 gap-4">
                {allImages.slice(1, 5).map((img, i) => (
                  <div
                    key={i}
                    className="album-card scale-up aspect-square cursor-pointer"
                    onClick={() => setLightboxIdx(i + 1)}
                  >
                    <img src={img} alt={`Stills ${i + 2}`} loading="lazy" />
                    <div className="overlay">
                      <span className="text-cream/70 text-[10px] font-montserrat">{i + 2}/{allImages.length}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom row */}
            {allImages.length > 5 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger">
                {allImages.slice(5).map((img, i) => (
                  <div
                    key={i}
                    className="album-card scale-up aspect-[4/3] cursor-pointer"
                    onClick={() => setLightboxIdx(i + 5)}
                  >
                    <img src={img} alt={`Stills ${i + 6}`} loading="lazy" />
                    <div className="overlay">
                      <span className="text-cream/70 text-[10px] font-montserrat">{i + 6}/{allImages.length}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ ABOUT STILLS ═══ */}
      <section className="py-20 md:py-28 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="fade-left">
              <div className="grid grid-cols-2 gap-4">
                <div className="album-card aspect-[3/4]">
                  <img src={sti03} alt="Portrait example" loading="lazy" />
                </div>
                <div className="album-card aspect-[3/4] mt-8">
                  <img src={sti04} alt="Portrait example" loading="lazy" />
                </div>
              </div>
            </div>
            <div className="fade-right">
              <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
                Why Choose Us
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl text-cream mb-6">
                Portraits That <span className="italic text-gold">Define You</span>
              </h2>
              <div className="space-y-4 text-cream/55 text-sm leading-[1.8] font-light">
                <p>
                  At Sabitha Studio, we believe a portrait should capture more than appearance — it should reveal character.
                  With 35+ years of experience, our photographers understand how to bring out the best in every subject.
                </p>
                <p>
                  Whether it's a professional headshot, family portrait, passport photo, or an artistic still composition,
                  we use expert lighting techniques and creative direction to deliver images that leave a lasting impression.
                </p>
                <ul className="space-y-2 text-cream/50">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Professional studio lighting setups
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Passport & visa photos with instant delivery
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Corporate headshots & portfolio shoots
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Creative fine art & conceptual portraits
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section
        className="parallax-section py-28 md:py-36"
        style={{ backgroundImage: `url(${sti02})` }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center fade-up">
          <p className="text-gold text-xs tracking-[0.3em] uppercase font-montserrat mb-3">Ready for Your Close-Up?</p>
          <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-6">
            Book a <span className="italic text-gold">Portrait Session</span>
          </h2>
          <div className="gold-line mb-8" />
          <p className="text-cream/60 text-base md:text-lg mb-12 max-w-2xl mx-auto font-light">
            Whether you need professional headshots, family portraits, or creative art pieces — we bring vision, expertise, and passion to every frame.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919703377033" className="btn-gold">Call +91-9703377033</a>
            <a href="https://wa.me/919703377033" target="_blank" rel="noopener noreferrer" className="btn-outline">WhatsApp Us</a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <Lightbox
          image={allImages[lightboxIdx]}
          onClose={() => setLightboxIdx(null)}
          onPrev={lightboxIdx > 0 ? () => setLightboxIdx((i) => i - 1) : null}
          onNext={lightboxIdx < allImages.length - 1 ? () => setLightboxIdx((i) => i + 1) : null}
        />
      )}
    </main>
  );
}
